import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ExampleDownloads } from "@/components/import/ExampleDownloads";

const PROMPT = `Create Magic: The Gathering custom cards as JSON for The Planar Foundry importer.
Use exactly this shape:
{"schema": "planar-foundry.import", "version": 1,
 "set": {"code": "ABC", "name": "Set name", "description": "", "creator": "", "release_date": "", "mechanics": [{"name": "", "reminder_text": ""}]},
 "cards": [{"import_id": "abc-001", "name": "", "mana_cost": "{2}{U}", "type_line": "Creature — Merfolk Wizard",
   "oracle_text": "", "flavor_text": "", "power": "2", "toughness": "2", "rarity": "common", "collector_number": "001",
   "artist": "", "keywords": [], "mechanics": [], "image": "abc-001.png"}]}
Rules: every mana symbol in braces like {2}{U}{U}; give every card a unique import_id and collector_number (like 001/020 inside a set);
you may add "supertypes" (e.g. ["Legendary"]), "types" and "subtypes" lists; list each card's mechanics in "mechanic_ids" and define them once in a top-level "mechanics_registry" {"id": {"kind": "...", "reminder_text": "..."}};
two-faced cards use "layout": "transform" or "modal_dfc" with "faces": [{...front, "image": "abc-002_front.png"}, {...back, "image": "abc-002_back.png"}];
split, adventure and flip cards use "faces" for both halves but one "image"; tokens use "layout": "token" and are listed in "related" by import_id.
Output only the JSON.`;

const ROWS = [
  ["Single-faced, saga, battle front, planeswalker, token, emblem", "1 complete card image"],
  ["Transform and modal double-faced cards", "1 image per face (front + back)"],
  ["Split, adventure", "1 image of the whole card (both halves visible)"],
  ["Flip cards", "1 image showing both halves side by side"],
];

export function FormatGuide() {
  const copy = () => navigator.clipboard?.writeText(PROMPT).then(() => toast.success("Prompt copied"));
  return (
    <section className="panel grid gap-8 p-6 lg:grid-cols-2" data-testid="import-format-guide">
      <div>
        <p className="eyebrow mb-3">Make cards with ChatGPT</p>
        <p className="mb-4 text-sm text-slate-400">Give this prompt to ChatGPT (or another tool) with your card ideas, then paste the JSON it returns above. Name your image files after each card's <code className="font-mono text-amber-200">import_id</code>.</p>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-[#2A3447] bg-black/40 p-4 font-mono text-[11px] leading-relaxed text-slate-300" data-testid="import-chatgpt-prompt">{PROMPT}</pre>
        <div className="mt-3 flex flex-wrap gap-3">
          <button className="btn-outline" onClick={copy} data-testid="import-copy-prompt"><Copy className="h-4 w-4" />Copy prompt</button>
        </div>
        <div className="mt-6"><ExampleDownloads testid="import-examples" /></div>
      </div>
      <div>
        <p className="eyebrow mb-3">Images every card needs</p>
        <p className="mb-4 text-sm text-slate-400">A card-face image is the entire finished card: art, frame, name, mana symbols, type line, text, stats and credits. The site shows it exactly as uploaded and does not read the text printed in it.</p>
        <dl className="divide-y divide-[#2A3447] text-sm">
          {ROWS.map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:justify-between sm:gap-4"><dt className="text-slate-300">{k}</dt><dd className="text-amber-200 sm:text-right">{v}</dd></div>
          ))}
        </dl>
        <p className="mt-4 text-xs text-slate-500">Images match by file names in the manifest, import ID (add _front / _back for two faces), set code + collector number (e.g. ABC_001.png), or a card name when it's unique. Validation checks the data's format, not full Magic rules correctness. Imported cards start private.</p>
      </div>
    </section>
  );
}
